export enum RecurrenceFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}

export class RecurrencePattern {
  frequency: RecurrenceFrequency = RecurrenceFrequency.DAILY;
  interval: number = 1;
  until?: string;
  count?: number;
  byDay?: number[]; // 0 = Sunday, 1 = Monday, etc.
  byMonthDay?: number[];
  byMonth?: number[];

  /**
   * Parse an iCalendar recurrence rule string into a RecurrencePattern object
   * @param recurrenceRule The iCalendar recurrence rule string (e.g., "FREQ=DAILY;INTERVAL=1")
   * @returns A RecurrencePattern object or null if the rule is invalid
   */
  static parseFromICalString(recurrenceRule: string): RecurrencePattern | null {
    if (!recurrenceRule) {
      return null;
    }

    const pattern = new RecurrencePattern();
    const parts = recurrenceRule.split(';').filter(part => part.trim() !== '');

    for (const part of parts) {
      const keyValue = part.split('=');
      if (keyValue.length !== 2) continue;

      const key = keyValue[0].toUpperCase().trim();
      const value = keyValue[1].trim();

      switch (key) {
        case 'FREQ':
          if (Object.values(RecurrenceFrequency).includes(value as RecurrenceFrequency)) {
            pattern.frequency = value as RecurrenceFrequency;
          }
          break;
        case 'INTERVAL':
          const interval = parseInt(value, 10);
          if (!isNaN(interval) && interval > 0) {
            pattern.interval = interval;
          }
          break;
        case 'UNTIL':
          try {
            // Store the UNTIL value as an ISO string
            // Handle both formats: with time (yyyyMMddTHHmmssZ) and without (yyyyMMdd)
            let untilDate: Date;
            
            if (value.includes('T')) {
              untilDate = new Date(
                Date.UTC(
                  parseInt(value.substring(0, 4), 10),
                  parseInt(value.substring(4, 6), 10) - 1,
                  parseInt(value.substring(6, 8), 10),
                  parseInt(value.substring(9, 11), 10),
                  parseInt(value.substring(11, 13), 10),
                  parseInt(value.substring(13, 15), 10)
                )
              );
            } else {
              untilDate = new Date(
                Date.UTC(
                  parseInt(value.substring(0, 4), 10),
                  parseInt(value.substring(4, 6), 10) - 1,
                  parseInt(value.substring(6, 8), 10),
                  23, 59, 59
                )
              );
            }
            
            pattern.until = untilDate.toISOString();
            console.log(`Parsed UNTIL date: ${value} -> ${pattern.until}`);
          } catch (e) {
            console.error('Error parsing UNTIL date:', e);
          }
          break;
        case 'COUNT':
          const count = parseInt(value, 10);
          if (!isNaN(count) && count > 0) {
            pattern.count = count;
          }
          break;
        case 'BYDAY':
          try {
            pattern.byDay = value.split(',')
              .map(d => RecurrencePattern.parseDayOfWeek(d.trim()))
              .filter(d => d !== -1);
          } catch (e) {
            console.error('Error parsing BYDAY:', e);
          }
          break;
        case 'BYMONTHDAY':
          pattern.byMonthDay = value.split(',')
            .map(s => {
              const day = parseInt(s.trim(), 10);
              return !isNaN(day) && day > 0 && day <= 31 ? day : -1;
            })
            .filter(d => d !== -1);
          break;
        case 'BYMONTH':
          pattern.byMonth = value.split(',')
            .map(s => {
              const month = parseInt(s.trim(), 10);
              return !isNaN(month) && month > 0 && month <= 12 ? month : -1;
            })
            .filter(m => m !== -1);
          break;
      }
    }

    // Validate that we have at least a frequency
    if (!pattern.frequency) {
      console.error('Invalid recurrence rule: missing FREQ parameter');
      return null;
    }

    return pattern;
  }

  /**
   * Parse a day of week string (e.g., "MO", "TU") into a number (0-6, where 0 = Sunday)
   * @param day The day of week string
   * @returns The day of week as a number (0-6) or -1 if invalid
   */
  private static parseDayOfWeek(day: string): number {
    switch (day.toUpperCase()) {
      case 'SU': return 0;
      case 'MO': return 1;
      case 'TU': return 2;
      case 'WE': return 3;
      case 'TH': return 4;
      case 'FR': return 5;
      case 'SA': return 6;
      default: return -1;
    }
  }
} 